import { type OlkOrganizeAndActErrorCodes } from 'owa-error-codes';
import {
    getSelectMailItemDatapoint,
    setSelectMailItemDatapoint,
    getSmiCustomDataRawArguments,
    resetSmiCustomDataRawArguments,
} from './selectMailItemDatapointGetterAndSetter';
import { addTimingsAndEndDatapointOnRender } from 'owa-analytics-shared';
import { DatapointStatus } from 'owa-analytics-types';
import { addExtrasCheckpointToSMI, resetExtrasCheckpoint } from './appendCheckpointToSMI';
import {
    MailRowDataPropertyGetter,
    type TableQueryType,
    getListViewState,
    getSelectedInboxPivot,
    isPriorityView,
    type MailFolderTableQuery,
} from 'owa-mail-list-store';
import { isReadingPanePositionOff } from 'owa-mail-layout';
import { getAggregationBucket } from 'owa-analytics-actions';
import { isReadingPaneConversationEnabled } from 'owa-mail-unstacked';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { userDate, differenceInDays, now } from 'owa-datetime';
import { getShouldAnimate } from 'owa-mail-list-item-animation-store';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getCopilotPrioritizePriority } from 'owa-mail-copilot-inbox-shared';
import type { PerformanceCoreDatapoint, PerformanceDatapoint } from 'owa-analytics';
import isMessageListBranchExpansionType from 'owa-mail-organization-settings/lib/utils/isMessageListBranchExpansionType';
import getRowExpansionState, {
    ExpansionState,
} from 'owa-mail-list-store/lib/selectors/getRowExpansionState';
import mailStore from 'owa-mail-store/lib/store/Store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

export default function endSelectMailItemDatapoint(
    contentType: string = '',
    shouldInvalidate?: boolean,
    overrideStatus?: DatapointStatus,
    error?: Error
): void {
    const selectMailItemDp = getSelectMailItemDatapoint();
    if (selectMailItemDp && !selectMailItemDp.hasEnded) {
        // Empty content type means there is no active selection at the time of ending the datapoint.
        if (shouldInvalidate || contentType === 'Empty') {
            selectMailItemDp.invalidate();
        } else {
            if (selectMailItemDp.getEventName() != 'SelectMailItemDeeplink') {
                addCustomDataToSelectMailItemDatapoint(selectMailItemDp, contentType);
            }
            if (
                overrideStatus === DatapointStatus.ServerError ||
                overrideStatus === DatapointStatus.ClientError
            ) {
                if (error === undefined) {
                    selectMailItemDp.endWithError(overrideStatus, '24_0079');
                } else {
                    selectMailItemDp.endWithError(overrideStatus, error);
                }
            } else {
                if (window.document && window.document.visibilityState == 'visible') {
                    addExtrasCheckpointToSMI(selectMailItemDp);
                    // Passing logOnly as false ends the datapoint after logging render end time if it has not already ended.
                    addTimingsAndEndDatapointOnRender(selectMailItemDp, false /* logOnly */);
                } else {
                    selectMailItemDp.end(
                        undefined /* duration */,
                        DatapointStatus.BackgroundSuccess
                    );
                }
            }
        }
    }
    if (selectMailItemDp) {
        setSelectMailItemDatapoint(undefined);
        resetSmiCustomDataRawArguments();
        resetExtrasCheckpoint();
    }
}

function addCustomDataToSelectMailItemDatapoint(
    dp: PerformanceCoreDatapoint | PerformanceDatapoint,
    contentType: string
) {
    const smiRawArgs = getSmiCustomDataRawArguments();
    if (smiRawArgs) {
        const { mailListItemSelectionSource, tableView, rowKey } = smiRawArgs;
        const timeStamp = MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
            rowKey,
            tableView
        );

        const daysAgo = timeStamp ? Math.max(0, differenceInDays(now(), userDate(timeStamp))) : -1;
        const autoSelectRow = getUserConfiguration()?.UserOptions?.ShowReadingPaneOnFirstLoad;
        const isConversationOnReadingPane =
            tableView.tableQuery.listViewType == 1 ? false : isReadingPaneConversationEnabled();
        const prioritizeScore = MailRowDataPropertyGetter.getCopilotInboxScore(rowKey, tableView);
        const priority = getCopilotPrioritizePriority(prioritizeScore);
        const copilotGeneratedContent = MailRowDataPropertyGetter.getCopilotGeneratedContent(
            rowKey,
            tableView
        );
        let itemPartsCount;
        if (isConversationOnReadingPane) {
            // Get number of item parts in the conversation
            const conversationId = MailRowDataPropertyGetter.getConversationId(rowKey, tableView);
            if (conversationId) {
                itemPartsCount =
                    mailStore().conversations.get(conversationId)?.conversationNodeIds.length;
            }
        }

        dp.addCustomData({
            mls: mailListItemSelectionSource,
            lv: tableView.tableQuery.listViewType,
            tq: tableView.tableQuery.type,
            search:
                tableView.tableQuery.type === 1 && (tableView.tableQuery as any).searchScope.kind,
            rpOn: !isReadingPanePositionOff(),
            rki: getAggregationBucket({
                value: tableView.rowKeys.indexOf(rowKey),
                buckets: [1, 2, 3, 5, 10, 15, 25, 35, 50, 75, 100, 150, 200, 250, 300, 500],
                exactMatches: [-1, 0],
            }), // Row key index
            rpS: isConversationOnReadingPane, // Reading pane is stacked
            fName: tableView.tableQuery.type === 0 && folderIdToName(tableView.tableQuery.folderId),
            age: daysAgo,
            ani: getShouldAnimate(),
            auto: autoSelectRow,
            contentType,
            rpExpAll: smiRawArgs.shouldExpandAllItemParts ? true : undefined, // Whether user has expanded all item parts in reading pane preference
            ipC: itemPartsCount,
            pri: priority,
            cgc: copilotGeneratedContent ?? '',
            tab: getSelectedInboxPivot(tableView),
            pv: isPriorityView(tableView.tableQuery as MailFolderTableQuery),
            expAllV2: isGlobalFeatureEnabled('rp-expand-v2'),
        });

        if (priority) {
            dp.addCosmosOnlyData(
                JSON.stringify({
                    priority,
                    itemId: MailRowDataPropertyGetter.getItemIds(rowKey, tableView)?.[0] ?? '',
                    conversationId:
                        MailRowDataPropertyGetter.getConversationId(rowKey, tableView) ?? '',
                    internetMessageId:
                        MailRowDataPropertyGetter.getInternetMessageId(rowKey, tableView) ?? '',
                    receivedDateTime:
                        MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKey, tableView) ?? '',
                })
            );
        }

        if (isMessageListBranchExpansionType()) {
            const rowExpansionState = getRowExpansionState(rowKey);
            const numberOfBranches =
                getListViewState().expandedConversationViewState?.forks?.length ?? 1;
            if (rowExpansionState === ExpansionState.BRANCHED && numberOfBranches > 1) {
                dp.addCustomData({
                    ip: 'fork', // item part
                });
            }
            if (rowExpansionState === ExpansionState.FULLY_EXPANDED) {
                dp.addCustomData({
                    ip: 'item', // item part
                });
            }
            // Add the number of forks to the datapoint
            if (numberOfBranches > 1) {
                dp.addCustomData({
                    forks: numberOfBranches,
                });
            }
        }
    }
}
